import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Megaphone,
  Plus,
  Calendar,
  Tag,
  Mail,
  Send,
  Users,
  Loader2,
  Edit,
  Trash2,
  Eye,
  Gift,
  HeartPulse,
  Sparkles,
  Bell,
  Coins
} from 'lucide-react';
import { toast } from 'sonner';

const EVENT_TYPES = [
  { value: 'promotion', label: 'Promotion', icon: Gift, color: 'bg-orange-100 text-orange-700' },
  { value: 'health_campaign', label: 'Campagne de santé', icon: HeartPulse, color: 'bg-green-100 text-green-700' },
  { value: 'new_product', label: 'Nouveau produit/service', icon: Sparkles, color: 'bg-blue-100 text-blue-700' },
  { value: 'announcement', label: 'Annonce', icon: Bell, color: 'bg-purple-100 text-purple-700' },
];

const AUDIENCE_TYPES = [
  { value: 'all_patients', label: 'Tous mes patients', description: 'RDV + Commandes' },
  { value: 'appointment_patients', label: 'Patients avec RDV', description: 'Historique des rendez-vous' },
  { value: 'product_customers', label: 'Clients produits', description: 'Historique des commandes' },
];

const FacilityEvents = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [emailCredits, setEmailCredits] = useState(0);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [audienceCount, setAudienceCount] = useState(0);
  const [loadingAudience, setLoadingAudience] = useState(false);
  
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'promotion',
    start_date: '',
    end_date: '',
    promo_code: '',
    discount_percentage: '',
    is_featured: false
  });
  
  const [campaignForm, setCampaignForm] = useState({
    target_audience: 'all_patients'
  });

  const loadEvents = useCallback(async () => {
    try {
      const [eventsRes, creditsRes] = await Promise.all([
        api.get('/events/my-events'),
        api.get('/events/campaign/credits')
      ]);
      setEvents(eventsRes.data.events || []);
      setEmailCredits(creditsRes.data.email_credits || 0);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const loadAudienceCount = async (audience) => {
    setLoadingAudience(true);
    try {
      const response = await api.get(`/events/campaign/audience-count?target_audience=${audience}`);
      setAudienceCount(response.data.audience_count || 0);
    } catch (error) {
      console.error('Error loading audience count:', error);
      setAudienceCount(0);
    } finally {
      setLoadingAudience(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.description) {
      toast.error('Veuillez remplir le titre et la description');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        ...eventForm,
        discount_percentage: eventForm.discount_percentage ? parseInt(eventForm.discount_percentage) : null
      };
      
      if (selectedEvent) {
        await api.put(`/events/${selectedEvent.id}`, payload);
        toast.success('Événement mis à jour');
      } else {
        await api.post('/events/', payload);
        toast.success('Événement créé');
      }
      
      setShowCreateModal(false);
      resetEventForm();
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Supprimer cet événement ?')) return;
    
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Événement supprimé');
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSendCampaign = async () => {
    if (audienceCount === 0) {
      toast.error('Aucun destinataire trouvé');
      return;
    }
    
    if (audienceCount > emailCredits) {
      toast.error(`Crédits insuffisants. Nécessaire: ${audienceCount}, Disponible: ${emailCredits}`);
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/events/campaign/send', {
        event_id: selectedEvent.id,
        target_audience: campaignForm.target_audience
      });
      
      toast.success(response.data.message);
      setShowCampaignModal(false);
      setEmailCredits(response.data.remaining_credits);
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const openEditModal = (event) => {
    setSelectedEvent(event);
    setEventForm({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      promo_code: event.promo_code || '',
      discount_percentage: event.discount_percentage?.toString() || '',
      is_featured: event.is_featured || false
    });
    setShowCreateModal(true);
  };

  const openCampaignModal = (event) => {
    setSelectedEvent(event);
    setCampaignForm({ target_audience: 'all_patients' });
    loadAudienceCount('all_patients');
    setShowCampaignModal(true);
  };

  const resetEventForm = () => {
    setSelectedEvent(null);
    setEventForm({
      title: '', description: '', event_type: 'promotion',
      start_date: '', end_date: '', promo_code: '', discount_percentage: '', is_featured: false
    });
  };

  const getEventTypeInfo = (type) => {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[3];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="facility-events-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Événements & Promotions
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Créez des événements et envoyez des campagnes email
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <Coins className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">{emailCredits} crédits email</span>
            </div>
            <Button
              onClick={() => { resetEventForm(); setShowCreateModal(true); }}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvel événement
            </Button>
          </div>
        </div>

        {/* Events Grid */}
        {events.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">Aucun événement</h3>
              <p className="text-slate-500 text-sm mt-1">
                Créez votre premier événement ou promotion
              </p>
              <Button 
                onClick={() => setShowCreateModal(true)} 
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer un événement
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => {
              const typeInfo = getEventTypeInfo(event.event_type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <Card key={event.id} className={`hover:shadow-md transition-shadow ${!event.is_active ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <Badge className={typeInfo.color}>
                        <TypeIcon className="w-3 h-3 mr-1" />
                        {typeInfo.label}
                      </Badge>
                      {event.is_featured && (
                        <Badge className="bg-red-100 text-red-700">Bannière</Badge>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-slate-900 line-clamp-2 mb-2">{event.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">{event.description}</p>
                    
                    {event.promo_code && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                        <p className="text-xs text-amber-600">Code promo</p>
                        <p className="font-mono font-bold text-amber-800">{event.promo_code}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-3">
                      {event.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(event.start_date).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {event.emails_sent || 0} envoyés
                      </span>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(event)}
                        className="flex-1"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openCampaignModal(event)}
                        className="flex-1 bg-blue-500 hover:bg-blue-600"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Envoyer
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteEvent(event.id)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
            <div>
              <Label>Type d&apos;événement *</Label>
              <Select
                value={eventForm.event_type}
                onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Titre *</Label>
              <Input
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Ex: -20% sur les consultations"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Détails de l'événement..."
                className="mt-1"
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {eventForm.event_type === 'promotion' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code promo</Label>
                  <Input
                    value={eventForm.promo_code}
                    onChange={(e) => setEventForm({ ...eventForm, promo_code: e.target.value.toUpperCase() })}
                    placeholder="EX: PROMO20"
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label>Réduction (%)</Label>
                  <Input
                    type="number"
                    value={eventForm.discount_percentage}
                    onChange={(e) => setEventForm({ ...eventForm, discount_percentage: e.target.value })}
                    placeholder="20"
                    min="1"
                    max="100"
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label>Afficher en bannière</Label>
                <p className="text-xs text-slate-500">Visible sur la page d&apos;accueil</p>
              </div>
              <Switch
                checked={eventForm.is_featured}
                onCheckedChange={(checked) => setEventForm({ ...eventForm, is_featured: checked })}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="flex-1 bg-red-500 hover:bg-red-600"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedEvent ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Campaign Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Envoyer la campagne</DialogTitle>
            <DialogDescription>
              {selectedEvent?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Audience cible</Label>
              <Select
                value={campaignForm.target_audience}
                onValueChange={(v) => {
                  setCampaignForm({ ...campaignForm, target_audience: v });
                  loadAudienceCount(v);
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <span className="font-medium">{type.label}</span>
                        <span className="text-slate-400 ml-2 text-xs">({type.description})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-400" />
                    <span className="text-sm">Destinataires</span>
                  </div>
                  {loadingAudience ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span className="text-2xl font-bold">{audienceCount}</span>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-amber-800">Crédits nécessaires</span>
                <span className="font-bold text-amber-800">{audienceCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-amber-800">Crédits disponibles</span>
                <span className={`font-bold ${emailCredits >= audienceCount ? 'text-green-600' : 'text-red-600'}`}>
                  {emailCredits}
                </span>
              </div>
              {emailCredits < audienceCount && (
                <p className="text-xs text-red-600 mt-2">
                  Crédits insuffisants. Achetez des crédits pour continuer.
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCampaignModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSendCampaign}
                disabled={sending || audienceCount === 0 || emailCredits < audienceCount}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer ({audienceCount} emails)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityEvents;
